import { useCallback, useEffect, useState } from 'react';
import { fetchMyAvatars, selectMyAvatar } from '../api/avatarApi';
import { normalizePlayerAvatarPayload } from '../avatar/playerAvatar.mjs';
import { runtime } from '../utils/runtime';

export const PLAYER_AVATAR_UPDATED_EVENT = 'pfandpirat:player-avatar-updated';

function initialAvatarData() {
  return normalizePlayerAvatarPayload({
    avatar_url: runtime.currentUserAvatarUrl,
    avatar_url_2x: runtime.currentUserAvatarUrl2x,
    wordpress_avatar_url: runtime.currentUserWordpressAvatarUrl,
    fallback_avatar_url: runtime.avatarFallbackUrl,
  });
}

function updateRuntimeAvatarSeed(data) {
  runtime.currentUserAvatarUrl = data.avatarUrl;
  runtime.currentUserAvatarUrl2x = data.avatarUrl2x;
  runtime.currentUserWordpressAvatarUrl = data.wordpressAvatarUrl;
  runtime.avatarFallbackUrl = data.fallbackAvatarUrl;
}

export function useAvatarWardrobe(options = {}) {
  const enabled = options.enabled !== false;
  const [avatarData, setAvatarData] = useState(initialAvatarData);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const applyData = useCallback((data) => {
    const normalized = normalizePlayerAvatarPayload(data);
    updateRuntimeAvatarSeed(normalized);
    setAvatarData(normalized);
    return normalized;
  }, []);

  const reload = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      setAvatarData(normalizePlayerAvatarPayload());
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchMyAvatars();
      applyData(data);
      return data;
    } catch (loadError) {
      setError(loadError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyData, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const handleAvatarUpdate = (event) => {
      if (event?.detail) applyData(event.detail);
    };
    window.addEventListener(PLAYER_AVATAR_UPDATED_EVENT, handleAvatarUpdate);
    return () => window.removeEventListener(PLAYER_AVATAR_UPDATED_EVENT, handleAvatarUpdate);
  }, [applyData]);

  const selectAvatar = useCallback(async (avatarId) => {
    setSaving(true);
    setError(null);

    try {
      const data = await selectMyAvatar(avatarId);
      const normalized = applyData(data);
      window.dispatchEvent(new CustomEvent(PLAYER_AVATAR_UPDATED_EVENT, { detail: normalized }));
      return normalized;
    } catch (saveError) {
      setError(saveError);
      throw saveError;
    } finally {
      setSaving(false);
    }
  }, [applyData]);

  return {
    avatarData,
    selectedAvatar: avatarData.selectedAvatar,
    avatars: avatarData.avatars,
    avatarUrl: avatarData.avatarUrl,
    unlockedAvatars: avatarData.avatars.filter((avatar) => avatar?.is_unlocked),
    lockedAvatars: avatarData.avatars.filter((avatar) => !avatar?.is_unlocked),
    loading,
    saving,
    error,
    reload,
    selectAvatar,
  };
}

export default useAvatarWardrobe;
